import { useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { useTheme } from 'styled-components';
import { ReactComponent as IconExcelReport } from '../../assets/icons/icon-excel-report.svg';
import { Button, NavHeader } from '../../components';
import { BaseFilterLeft } from '../../components/BaseFilterLeft';
import { useSolicitation } from '../../context/solicitation';
import { Body, Container, Content } from './styles';
import { useApp, useAuth } from '../../context';
import { ProfileType, TutorialType, TypeUser } from '../../types/enums';
import { InputFilter } from '../../components/Inputs/InputFilter';
import { CardSolicitationDetails } from '../../components/Cards/CardSolicitationDetails';
import { useAlert } from '../../hooks/useAlert';
import { useReport } from '../../context/report';
import JoyrideTour from '../../components/JoyrideTour';
import { yourSolicitationsSteps } from '../../constants/Tour/Steps/yourSolicitationsSteps';
import TitlePage from '../../components/TitlePage';
import { mockSolicitation } from '../../constants/Tour/Mocks/solicitationMock';

export function YourSolicitations() {
  const { isGeneratingReport, handleGenerateSolicitationReport } = useReport();
  const alert = useAlert();
  const { setLoading } = useApp();
  const { user } = useAuth();
  const { colors } = useTheme();
  const [runningTutorial, setRunningTutorial] = useState(false);
  const {
    solicitations,
    totalSolicitations,
    setSolicitations,
    filter,
    setFilter,
    isLoadingSolicitation,
    isLoadingSolicitationByCollaborator,
    isFetchingNextPageSolicitation,
    hasNextPageSolicitation,
    hasNextPageSolicitationByCollaborator,
    fetchNextPageSolicitation,
    isFetchingNextPageSolicitationByCollaborator,
    fetchNextPageSolicitationByCollaborator,
  } = useSolicitation();
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearch = (text: string) => {
    setSearchTerm(text);
  };

  //ajustar ao inserir o solicitação com anexo
  const filteredSolicitations = solicitations.filter((solicitation) => {
    const { protocolNumber, correspondence, specialty, createdAt, dateLimit } = solicitation;
    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    return (
      protocolNumber.toLowerCase().includes(lowerCaseSearchTerm) ||
      correspondence?.name.toLowerCase().includes(lowerCaseSearchTerm) ||
      false ||
      specialty?.name.toLowerCase().includes(lowerCaseSearchTerm) ||
      false ||
      new Date(createdAt).toLocaleDateString('pt-BR').includes(lowerCaseSearchTerm) ||
      new Date(dateLimit).toLocaleDateString('pt-BR').includes(lowerCaseSearchTerm)
    );
  });

  const handleGenerateReport = () => {
    if (isGeneratingReport) return;
    handleGenerateSolicitationReport({
      ...filter,
      isCollaborator: user.type === TypeUser.COLLABORATOR,
    });
  };

  useEffect(() => {
    if (user.type === TypeUser.COLLABORATOR) {
      setLoading(
        isLoadingSolicitationByCollaborator || isFetchingNextPageSolicitationByCollaborator,
      );
    } else {
      setLoading(isLoadingSolicitation || isFetchingNextPageSolicitation);
    }
  }, [
    isFetchingNextPageSolicitation,
    isFetchingNextPageSolicitationByCollaborator,
    isLoadingSolicitation,
    isLoadingSolicitationByCollaborator,
    setLoading,
    user.type,
  ]);

  useEffect(() => {
    setSolicitations(
      solicitations.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    );
  }, [solicitations, setSolicitations]);

  useEffect(() => {
    if (!user?.tutorialProgress?.some((p) => p === TutorialType.YOUR_SOLICITATIONS)) {
      setRunningTutorial(true);
    }
  }, [user.tutorialProgress]);

  return (
    <>
      <NavHeader typeHeader="company" />
      <Body>
        <JoyrideTour
          type={TutorialType.YOUR_SOLICITATIONS}
          steps={yourSolicitationsSteps}
          run={runningTutorial}
          onCallbackFinished={() => setRunningTutorial(false)}
        />
        <BaseFilterLeft
          onChangeFilter={(solicitationFilter) => {
            setFilter(solicitationFilter);
          }}
          displayOptions={{
            client:
              [TypeUser.COMPANY, TypeUser.ADM].includes(user.type) ||
              (user.collaboratorInfo?.profileType !== undefined
                ? [ProfileType.APPROVER, ProfileType.OPERATOR].includes(
                  user.collaboratorInfo?.profileType!,
                )
                : false),
            processNumber: true,
            correspondent: false,
            correspondenceType: true,
            correspondence: true,
            date: true,
            especilty: true,
            status: true,
            audienceType: true,
            folderName: true,
            partName: true,
            court: false,
            protocol: true,
            paymentStatus: true,
            district: true,
          }}
        />
        <Container>
          <div className="header">
            <div className="titleSolicitation">
              <TitlePage
                title={`Solicitações geradas `}
                onRequestOpen={() => {
                  setRunningTutorial(true);
                }}
                id="start-item"
              />
              {solicitations.length > 0 && (
                <span>
                  ({solicitations.length} de {totalSolicitations})
                </span>
              )}
            </div>
            <div className="searchExportContainer">
              <div className="searchExportContent">
                <InputFilter onSearch={handleSearch} />
                <Button
                  id="excel-report"
                  text="Extrair relatório"
                  LeftComponent={IconExcelReport}
                  styleLeftComponent={{ fill: colors.tertiary_500 }}
                  style={{ fontSize: '1.20rem', display: 'flex', alignItems: 'center' }}
                  dimensions={{
                    width: '100%',
                    height: '1.25rem',
                  }}
                  positionLeftElement={5}
                  border={{ color: 'background_white', width: 0 }}
                  color="tertiary_500"
                  textHoverColor="tertiary_500"
                  backgroundHoverColor="background_white"
                  disabled={isGeneratingReport}
                  onClick={handleGenerateReport}
                />
              </div>
            </div>
          </div>
          <Content>
            <div className="contentSolicitations">
              {runningTutorial ? (
                <div id="solicitations">
                  <CardSolicitationDetails
                    solicitationParam={mockSolicitation}
                    isTutorial
                    isBorder
                  />
                </div>
              ) : filteredSolicitations.length ? (
                filteredSolicitations?.map((solicitation) => (
                  <CardSolicitationDetails
                    key={solicitation.id}
                    solicitationParam={solicitation}
                    isBorder
                  />
                ))
              ) : (
                <Typography align="center" color={colors.secondary}>
                  Nenhuma solicitação encontrada...
                </Typography>
              )}
            </div>
            {user.type === TypeUser.COLLABORATOR
              ? hasNextPageSolicitationByCollaborator && (
                <Grid container justifyContent="center" padding="1rem 1rem">
                  <Button
                    text="Carregar mais..."
                    onClick={fetchNextPageSolicitationByCollaborator}
                    dimensions={{ height: '2rem', width: '10rem' }}
                    color="primary"
                    textHoverColor="tertiary_500"
                    backgroundHoverColor="transparent"
                    background="transparent"
                    border={{ color: 'transparent' }}
                    loading={
                      isLoadingSolicitationByCollaborator ||
                      isFetchingNextPageSolicitationByCollaborator
                    }
                    disabled={
                      isLoadingSolicitationByCollaborator ||
                      isFetchingNextPageSolicitationByCollaborator
                    }
                  />
                </Grid>
              )
              : hasNextPageSolicitation && (
                <Grid container justifyContent="center" padding="1rem 1rem">
                  <Button
                    text="Carregar mais..."
                    onClick={fetchNextPageSolicitation}
                    dimensions={{ height: '2rem', width: '10rem' }}
                    color="primary"
                    textHoverColor="tertiary_500"
                    backgroundHoverColor="transparent"
                    background="transparent"
                    border={{ color: 'transparent' }}
                    loading={isFetchingNextPageSolicitation || isLoadingSolicitation}
                    disabled={isFetchingNextPageSolicitation || isLoadingSolicitation}
                  />
                </Grid>
              )}
          </Content>
        </Container>
      </Body>
    </>
  );
}
